import { ArrowClockwiseIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";

import { ApiError, apiRequest } from "../api/client";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { LoadingState } from "../components/LoadingState";
import type { LeadSubmission } from "../types";

const statusLabels: Record<LeadSubmission["status"], string> = {
  new: "Новая",
  read: "Прочитана",
  archived: "Архив",
};

export function LeadsPage() {
  const [leads, setLeads] = useState<LeadSubmission[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    LeadSubmission["status"] | ""
  >("");
  const [noteDraft, setNoteDraft] = useState("");
  const filteredLeads = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return leads.filter((lead) => {
      if (statusFilter && lead.status !== statusFilter) return false;
      if (!normalized) return true;
      return [
        lead.name,
        lead.email,
        lead.phone,
        lead.company,
        lead.topic,
        lead.project_format,
        lead.source,
        lead.path,
        utmText(lead.utm_json),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalized);
    });
  }, [leads, query, statusFilter]);
  const selected =
    filteredLeads.find((lead) => lead.id === selectedId) ?? filteredLeads[0];
  const selectedNote =
    selected && typeof selected.metadata_json.admin_note === "string"
      ? selected.metadata_json.admin_note
      : "";

  async function load() {
    setLoading(true);
    setError("");
    try {
      const result = await apiRequest<LeadSubmission[]>("/api/v1/admin/leads");
      setLeads(result);
      setSelectedId((current) => current || result[0]?.id || "");
    } catch {
      setError("Не удалось загрузить заявки");
    } finally {
      setLoading(false);
    }
  }

  async function updateStatus(
    lead: LeadSubmission,
    status: LeadSubmission["status"],
    adminNote = selectedNote,
  ) {
    try {
      const updated = await apiRequest<LeadSubmission>(
        `/api/v1/admin/leads/${lead.id}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({ status, admin_note: adminNote }),
        },
      );
      setLeads((items) =>
        items.map((item) => (item.id === updated.id ? updated : item)),
      );
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось обновить статус заявки",
      );
    }
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    setNoteDraft(selectedNote);
  }, [selected?.id, selectedNote]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Входящие</span>
          <h1>Заявки с сайта</h1>
          <p>
            Всё, что человек написал в форме, появляется здесь вместе с
            источником, страницей и UTM-метками.
          </p>
        </div>
        <Button onClick={() => void load()} tone="secondary">
          <ArrowClockwiseIcon size={16} /> Обновить
        </Button>
      </header>

      {error ? <div className="notice notice--error">{error}</div> : null}
      {loading ? (
        <LoadingState rows={8} />
      ) : leads.length ? (
        <>
          <div className="table-toolbar table-toolbar--leads">
            <label>
              <span>Поиск</span>
              <input
                onChange={(event) => setQuery(event.target.value)}
                placeholder="имя, email, utm, source"
                value={query}
              />
            </label>
            <label>
              <span>Статус</span>
              <select
                onChange={(event) =>
                  setStatusFilter(
                    event.target.value as LeadSubmission["status"] | "",
                  )
                }
                value={statusFilter}
              >
                <option value="">Все заявки</option>
                {Object.entries(statusLabels).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <strong>{filteredLeads.length} заявок</strong>
            <Button
              onClick={() =>
                downloadCsv(
                  "unicore-leads.csv",
                  filteredLeads.map((lead) => ({
                    status: statusLabels[lead.status],
                    name: lead.name,
                    email: lead.email ?? "",
                    phone: lead.phone ?? "",
                    company: lead.company ?? "",
                    topic: lead.topic ?? "",
                    project_format: lead.project_format ?? "",
                    source: lead.source ?? "",
                    path: lead.path ?? "",
                    visitor_id: lead.visitor_id ?? "",
                    visit_id: lead.visit_id ?? "",
                    session_id: lead.session_id ?? "",
                    referrer: lead.referrer ?? "",
                    landing_url: lead.landing_url ?? "",
                    spam_status: lead.spam_status,
                    spam_score: String(lead.spam_score),
                    utm: utmText(lead.utm_json),
                    created_at: lead.created_at,
                  })),
                )
              }
              tone="secondary"
            >
              CSV
            </Button>
          </div>
          <section className="leads-layout">
            <div className="leads-list">
              {filteredLeads.map((lead) => (
                <button
                  className={lead.id === selected?.id ? "is-active" : ""}
                  key={lead.id}
                  onClick={() => setSelectedId(lead.id)}
                  type="button"
                >
                  <span className={`lead-dot lead-dot--${lead.status}`} />
                  <strong>{lead.name}</strong>
                  <small>{leadContact(lead)}</small>
                  <em>{new Date(lead.created_at).toLocaleString()}</em>
                </button>
              ))}
              {!filteredLeads.length ? (
                <div className="inline-empty">
                  По фильтрам ничего не найдено.
                </div>
              ) : null}
            </div>
            {selected ? (
              <article className="lead-detail">
                <header>
                  <div>
                    <span className={`status status--${selected.status}`}>
                      {statusLabels[selected.status]}
                    </span>
                    <h2>{selected.name}</h2>
                    <p>{leadContact(selected)}</p>
                  </div>
                  <div className="lead-actions">
                    <Button
                      disabled={selected.status === "read"}
                      onClick={() =>
                        void updateStatus(selected, "read", noteDraft)
                      }
                      tone="secondary"
                    >
                      Прочитана
                    </Button>
                    <Button
                      disabled={selected.status === "archived"}
                      onClick={() =>
                        void updateStatus(selected, "archived", noteDraft)
                      }
                      tone="quiet"
                    >
                      В архив
                    </Button>
                  </div>
                </header>

                <dl className="lead-fields">
                  <div>
                    <dt>Формат</dt>
                    <dd>{selected.project_format || "—"}</dd>
                  </div>
                  <div>
                    <dt>Тема</dt>
                    <dd>{selected.topic || "—"}</dd>
                  </div>
                  <div>
                    <dt>Страница</dt>
                    <dd>{selected.path || "—"}</dd>
                  </div>
                  <div>
                    <dt>Источник</dt>
                    <dd>{selected.source || "direct"}</dd>
                  </div>
                  <div>
                    <dt>UTM</dt>
                    <dd>{utmText(selected.utm_json)}</dd>
                  </div>
                  <div>
                    <dt>Форма</dt>
                    <dd>{String(selected.metadata_json.form_id ?? "—")}</dd>
                  </div>
                  <div>
                    <dt>Visitor</dt>
                    <dd>
                      {selected.visitor_id ||
                        String(selected.metadata_json.visitor_id ?? "—")}
                    </dd>
                  </div>
                  <div>
                    <dt>Visit / session</dt>
                    <dd>
                      {selected.visit_id || "—"}
                      {selected.session_id ? ` · ${selected.session_id}` : ""}
                    </dd>
                  </div>
                  <div>
                    <dt>Landing</dt>
                    <dd>
                      {selected.landing_url ||
                        String(selected.metadata_json.landing_url ?? "—")}
                    </dd>
                  </div>
                  <div>
                    <dt>Referrer</dt>
                    <dd>{selected.referrer || "—"}</dd>
                  </div>
                  <div>
                    <dt>Spam check</dt>
                    <dd>
                      {selected.spam_status} · {selected.spam_score}
                    </dd>
                  </div>
                  <div>
                    <dt>IP / UA</dt>
                    <dd>
                      {selected.ip_address || "—"}
                      {selected.user_agent
                        ? ` · ${selected.user_agent.slice(0, 90)}`
                        : ""}
                    </dd>
                  </div>
                </dl>

                <section className="lead-message">
                  <span className="eyebrow">Сообщение</span>
                  <p>
                    {selected.message || "Пользователь не оставил описание."}
                  </p>
                </section>
                <section className="lead-message lead-timeline">
                  <span className="eyebrow">Цепочка заявки</span>
                  <div>
                    <strong>Создана</strong>
                    <span>
                      {new Date(selected.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <strong>Landing</strong>
                    <span>{selected.landing_url || selected.path || "—"}</span>
                  </div>
                  <div>
                    <strong>Источник</strong>
                    <span>
                      {selected.source || selected.referrer || "direct"}
                    </span>
                  </div>
                  <div>
                    <strong>UTM</strong>
                    <span>{utmText(selected.utm_json)}</span>
                  </div>
                  <div>
                    <strong>Антиспам</strong>
                    <span>
                      {selected.spam_status} · score {selected.spam_score}
                    </span>
                  </div>
                </section>
                <section className="lead-message lead-note">
                  <span className="eyebrow">Внутренний комментарий</span>
                  <textarea
                    onChange={(event) => setNoteDraft(event.target.value)}
                    placeholder="Что важно помнить по заявке, кому передать, следующий шаг…"
                    value={noteDraft}
                  />
                  <Button
                    disabled={noteDraft.trim() === selectedNote}
                    onClick={() =>
                      void updateStatus(selected, selected.status, noteDraft)
                    }
                    tone="secondary"
                  >
                    Сохранить комментарий
                  </Button>
                </section>
              </article>
            ) : null}
          </section>
        </>
      ) : (
        <EmptyState
          title="Заявок пока нет"
          description="Отправьте форму на публичной странице контактов — заявка появится здесь."
        />
      )}
    </div>
  );
}

function leadContact(lead: LeadSubmission) {
  return [lead.phone, lead.email].filter(Boolean).join(" · ") || "—";
}

function utmText(value: Record<string, unknown>) {
  const entries = Object.entries(value).filter(([, item]) => Boolean(item));
  if (!entries.length) return "—";
  return entries.map(([key, item]) => `${key}: ${String(item)}`).join(" · ");
}

function downloadCsv(filename: string, rows: Record<string, string>[]) {
  const headers = Object.keys(rows[0] ?? {});
  const escape = (value: string) => `"${value.replaceAll('"', '""')}"`;
  const csv = [
    headers.join(","),
    ...rows.map((row) =>
      headers.map((key) => escape(row[key] ?? "")).join(","),
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
